import { Avatar } from "antd";
import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Select,
  Upload,
  Switch,
  message,
  Row,
  Col,
} from "antd";
import { Button } from "reactstrap";
import {
  InboxOutlined,
  PaperClipOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Swal from "sweetalert2";
import { SelectOption } from "../../form-components/SelectComponent";
import { useSelector } from "react-redux";
import { issueApis } from "../../../api/Issue";
import dayjs from "dayjs";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import {
  AddAccess,
  EditAccess,
  CancelAccess,
} from "../../../components/accessControl/AccessControl";

const { Option } = Select;

// Custom Quill modules and formats
const Quill = ReactQuill.Quill;
const FontAttributor = Quill.import("formats/font") as any;
FontAttributor.whitelist = [
  "Roboto",
  "Arial",
  "Georgia",
  "Times-New-Roman",
  "Verdana",
  "Courier-New",
];
Quill.register(FontAttributor, true);

const SizeAttributor = Quill.import("formats/size") as any;
SizeAttributor.whitelist = [
  "6px",
  "10px",
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "32px",
  "48px",
];
Quill.register(SizeAttributor, true);

const toolbarOptions = [
  [{ font: [] }],
  [{ size: [] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  ["blockquote", "code-block"],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ script: "sub" }, { script: "super" }],
  [{ indent: "-1" }, { indent: "+1" }],
  ["link", "image", "video"],
  ["clean"],
];

const modules = {
  toolbar: toolbarOptions,
};

interface IssueResolutionTabProps {
  activeTab: string;
  statusOptions?: SelectOption[];
  issueDetails: any;
  updateIssue?: (data: any) => Promise<void>;
  isFromWorkUI?: boolean;
}

const VALID_STATUSES = ["Closed", "Open", "Resolved", "RESOLVED", "UnderQC", "InProgress"];

const DEFAULT_STATUS_OPTIONS: SelectOption[] = [
  { value: "Open", label: "Open" },
  { value: "InProgress", label: "In Progress" },
  { value: "Resolved", label: "Resolved" },
  { value: "UnderQC", label: "Under QC" },
  { value: "Closed", label: "Closed" },
];

// Ensure a "Resolved" option is always present in the Resolution tab status
// dropdown. The API expects the proper-case value "Resolved" — older code
// passed "RESOLVED" (all caps); accept either while normalizing to "Resolved".
const ensureResolvedInOptions = (options: SelectOption[]): SelectOption[] => {
  const hasResolved = options.some(
    (o) => String(o.value).toUpperCase() === "RESOLVED"
  );
  if (hasResolved) {
    return options.map((o) =>
      String(o.value).toUpperCase() === "RESOLVED"
        ? { ...o, value: "Resolved" }
        : o
    );
  }
  return [{ value: "Resolved", label: "Resolved" }, ...options];
};

const IssueResolutionTab: React.FC<IssueResolutionTabProps> = ({
  activeTab,
  statusOptions = DEFAULT_STATUS_OPTIONS,
  issueDetails,
  updateIssue = async () => {},
  isFromWorkUI = false,
}) => {
  const resolutionStatusOptions = ensureResolvedInOptions(
    statusOptions?.length ? statusOptions : DEFAULT_STATUS_OPTIONS
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [resolutionText, setResolutionText] = useState("");
  const [status, setStatus] = useState(statusOptions?.[0]?.value || "Open");
  const [attachedFiles, setAttachedFiles] = useState<any[]>([]);
  const [isExpandedEditor, setIsExpandedEditor] = useState(false);
  const [isEditorDark, setIsEditorDark] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const isAssignee = currentUser?.id === issueDetails?.assignedTo;
  const isAdmin =
    (currentUser?.genericRole || "").toLowerCase() === "admin";
  const canAddResolution = isAssignee || isAdmin;

  // Local mirror of issueResolutions so newly-added resolutions appear in the
  // list immediately even if the parent doesn't re-fetch the issue.
  const [localResolutions, setLocalResolutions] = useState<any[]>(
    issueDetails?.issueResolutions || []
  );

  useEffect(() => {
    setLocalResolutions(issueDetails?.issueResolutions || []);
  }, [issueDetails?.issueResolutions]);

  const handleOpenModal = () => {
    if (isFromWorkUI) return;
    setIsModalOpen(true);
    setResolutionText("");
    setAttachedFiles([]);
    // Default the modal's status dropdown to the issue's current status
    // (the one shown on the main view) so it carries forward — fall back to
    // the first available option, then to "Resolved" as a last resort.
    const currentIssueStatus = issueDetails?.issueStatus;
    const isCurrentInOptions =
      currentIssueStatus &&
      resolutionStatusOptions.some(
        (o) => String(o.value) === String(currentIssueStatus),
      );
    setStatus(
      isCurrentInOptions
        ? currentIssueStatus
        : resolutionStatusOptions?.[0]?.value ?? "Resolved",
    );
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsExpandedEditor(false);
    setAttachedFiles([]);
    setResolutionText("");
  };

  const handleResolve = async () => {
    if (isFromWorkUI) return;
    if (!resolutionText.trim()) {
      messageApi.error("Resolution text is required");
      return;
    }

    if (!canAddResolution) {
      Swal.fire({
        icon: "warning",
        title: "Access Denied",
        text: "Only the assigned user or an admin can resolve this issue.",
        confirmButtonText: "Ok",
        customClass: { container: "border-class" },
      });
      return;
    }

    try {
      const convertHtmlToMarkdown = require("html-to-markdown").convert;
      const markdownText = convertHtmlToMarkdown(resolutionText);

      const response = await issueApis.changeIssueStatus(
        issueDetails.issueId,
        status,
        markdownText
      );
      if (response.status) {
        Swal.fire({
          icon: "success",
          title: "Resolution Submitted!",
          text: "The resolution has been recorded.",
          confirmButtonText: "Ok",
          customClass: { container: "border-class" },
        });
        setLocalResolutions((prev) => [
          ...prev,
          {
            resolutionText: markdownText,
            resolutionStatus: status,
            createdAt: new Date().toISOString(),
          },
        ]);
        setIsModalOpen(false);
        setResolutionText("");
        updateIssue({}); // Refresh issue data
      } else {
        throw new Error(response.message || "Failed to resolve issue");
      }
    } catch (error) {
      console.error("Resolution error:", error);
      if (error instanceof Error && error.message) {
        messageApi.error(error.message);
      } else {
        messageApi.error("Failed to submit resolution. Please try again.");
      }
    }
  };

  const handleFileUpload = (info: any) => {
    if (isFromWorkUI) return;
    if (info.file.status !== "uploading") {
      // When beforeUpload returns false, antd leaves info.file as the raw
      // File and does NOT populate originFileObj — so previously the chips
      // were rendering with an undefined name. Fall back to info.file.
      const fileObj = info.file.originFileObj || info.file;
      if (fileObj && fileObj.name) {
        setAttachedFiles((prev) => [...prev, fileObj]);
      }
    }
  };

  const handleRemoveFile = (fileToRemove: any) => {
    setAttachedFiles((prev) =>
      prev.filter(
        (file) =>
          file !== (fileToRemove?.originFileObj || fileToRemove),
      )
    );
  };

  const toggleEditorTheme = () => setIsEditorDark((prev) => !prev);

  const getInitials = (name: string) => {
    if (!name) return "";
    const names = name.split(" ");
    let initials = names[0].substring(0, 1).toUpperCase();
    if (names.length > 1)
      initials += names[names.length - 1].substring(0, 1).toUpperCase();
    return initials;
  };

  if (activeTab !== "Resolution") return null;

  const hasResolutions = localResolutions.length > 0;

  return (
    <div className="px-3 py-4">
      {contextHolder}

      {hasResolutions ? (
        <div className="resolution-list">
          {localResolutions.map((resolution: any, index: number) => (
            <div key={index} className="resolution-item mb-3 p-3 border rounded">
              <div className="d-flex align-items-start">
                <Avatar
                  size={40}
                  style={{ backgroundColor: "#f56a00", marginRight: 12 }}
                >
                  {getInitials(issueDetails.IssueAssignedToName || "N/A")}
                </Avatar>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">
                      {issueDetails.IssueAssignedToName || "Unknown User"}
                    </span>
                  </div>
                  <div className="mt-1">
                    <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                      {resolution.resolutionText}
                    </ReactMarkdown>
                  </div>
                  <div className="text-muted small mt-1">
                    {dayjs(resolution.createdAt).format("hh:mm A, DD MMM YYYY")}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {!isFromWorkUI && canAddResolution && (
            <div className="text-center mt-3">
              {isAdmin ? (
                // Admins bypass the AddAccess permission gate — the
                // accessibility config doesn't always include Issues > add
                // for admin roles, which was hiding the button.
                <Button className="primary" onClick={handleOpenModal}>
                  Add Resolution
                </Button>
              ) : (
                <AddAccess moduleName="Issues">
                  <Button className="primary" onClick={handleOpenModal}>
                    Add Resolution
                  </Button>
                </AddAccess>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center">
          <h6 className="mb-2">No Resolution Found</h6>
          {!isFromWorkUI && canAddResolution ? (
            <>
              <p className="text-muted">File a resolution for the issue.</p>
              {isAdmin ? (
                <Button className="primary" onClick={handleOpenModal}>
                  Resolve Issue
                </Button>
              ) : (
                <AddAccess moduleName="Issues">
                  <Button className="primary" onClick={handleOpenModal}>
                    Resolve Issue
                  </Button>
                </AddAccess>
              )}
            </>
          ) : (
            <p className="text-muted">
              {isFromWorkUI
                ? "Resolution details"
                : "Only the assigned user or an admin can add resolutions."}
            </p>
          )}
        </div>
      )}

      {!isFromWorkUI && canAddResolution && (
        <Modal
          open={isModalOpen}
          onCancel={handleCancel}
          footer={null}
          width={isExpandedEditor ? "100%" : 800}
          centered={!isExpandedEditor}
          style={
            isExpandedEditor
              ? { top: 0, padding: 0, margin: 0, maxWidth: "100vw" }
              : {}
          }
          bodyStyle={
            isExpandedEditor
              ? {
                  height: "calc(100vh - 60px)",
                  padding: "24px 32px",
                  display: "flex",
                  flexDirection: "column",
                  background: isEditorDark ? "#333" : "#fff",
                }
              : {}
          }
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: isExpandedEditor ? 12 : 24,
              paddingBottom: isExpandedEditor ? 0 : 16,
              borderBottom: isExpandedEditor ? "none" : "1px solid #eee",
              flexShrink: 0,
            }}
          >
            {!isExpandedEditor && <h5 className="mb-0">Resolve Issue</h5>}
            {isExpandedEditor && (
              <Switch checked={isEditorDark} onChange={toggleEditorTheme} />
            )}
          </div>

          <Form
            layout="vertical"
            style={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
          >
            {!isExpandedEditor && (
              <Form.Item label="Status">
                <Select
                  value={status}
                  onChange={setStatus}
                  style={{ width: "100%" }}
                >
                  {resolutionStatusOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item
              label="Resolution"
              required
              style={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                marginBottom: isExpandedEditor ? 0 : 24,
              }}
            >
              <ReactQuill
                value={resolutionText}
                onChange={setResolutionText}
                placeholder="Enter resolution details..."
                modules={modules}
                theme="snow"
                style={{
                  flexGrow: 1,
                  minHeight: isExpandedEditor ? "calc(100% - 40px)" : "200px",
                  borderRadius: "4px",
                  display: "flex",
                  flexDirection: "column",
                  border: isEditorDark ? "1px solid #555" : "1px solid #d9d9d9",
                }}
                className={
                  isEditorDark ? "dark-quill-theme" : "light-quill-theme"
                }
              />
            </Form.Item>

            {!isExpandedEditor && (
              <>
                <Row className="mt-3 align-items-center">
                  <Col md={6}>
                    <Upload
                      name="files"
                      multiple
                      showUploadList={false}
                      beforeUpload={() => false}
                      onChange={handleFileUpload}
                      onRemove={handleRemoveFile}
                    >
                      <Button
                        icon={<InboxOutlined />}
                        size="small"
                        style={{ fontSize: "13px", padding: "0 8px" }}
                      >
                        Attach Files
                      </Button>
                    </Upload>
                  </Col>
                  <Col
                    md={6}
                    className="d-flex justify-content-end align-items-center gap-2"
                  >
                    <Button
                      onClick={handleCancel}
                      style={{
                        fontSize: "12px",
                        padding: "0 12px",
                        height: "28px",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="primary"
                      onClick={handleResolve}
                      style={{
                        fontSize: "12px",
                        padding: "0 12px",
                        height: "28px",
                      }}
                    >
                      Resolve Issue
                    </Button>
                  </Col>
                </Row>
                {attachedFiles.length > 0 && (
                  <Row className="mt-2">
                    <Col md={12}>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                        }}
                      >
                        {attachedFiles.map((file: any, idx: number) => (
                          <span
                            key={`${file?.name}-${idx}`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              maxWidth: "100%",
                              padding: "2px 10px",
                              background: "#f5f5f5",
                              border: "1px solid #e8e8e8",
                              borderRadius: 12,
                              fontSize: 12,
                              lineHeight: "20px",
                              wordBreak: "break-all",
                            }}
                            title={file?.name}
                          >
                            <PaperClipOutlined
                              style={{ fontSize: 12, flexShrink: 0 }}
                            />
                            <span>{file?.name}</span>
                            <CloseOutlined
                              style={{
                                fontSize: 10,
                                cursor: "pointer",
                                color: "#888",
                                flexShrink: 0,
                              }}
                              onClick={() =>
                                setAttachedFiles((prev) =>
                                  prev.filter((f) => f !== file),
                                )
                              }
                            />
                          </span>
                        ))}
                      </div>
                    </Col>
                  </Row>
                )}
              </>
            )}

            {isExpandedEditor && (
              <div
                style={{
                  marginTop: 24,
                  paddingTop: 16,
                  borderTop: "1px solid #eee",
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: 16,
                  flexShrink: 0,
                }}
              >
                <Button
                  className="primary"
                  onClick={handleResolve}
                  style={{ padding: "6px 20px" }}
                >
                  Save
                </Button>
                <Button onClick={handleCancel} style={{ padding: "6px 20px" }}>
                  Cancel
                </Button>
              </div>
            )}
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default IssueResolutionTab;